import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { ErrorNotice, Notice } from "../components/ui/Feedback";
import { Field } from "../components/ui/Input";
import { Datum, SectionTitle } from "../components/ui/Text";
import { post } from "../lib/api";
import { MIN_PASSWORD_LENGTH, passwordIssues } from "../lib/operator";
import { groupPermissions, permissionCount } from "../lib/permissions";
import { useSession } from "../lib/session";
import type { PasswordChange } from "../lib/types";

/**
 * The signed-in operator's own account.
 *
 * Reached from the name in the rail's footer rather than from the nav list: it is
 * not a place anybody goes during a shift, and it belongs to the person rather than
 * to the deployment.
 *
 * ## Changing a password
 *
 * The current password is asked for even though the session already proves who
 * this is. A session is a cookie on a laptop in a depot office, and "somebody sat
 * down at an unlocked screen" should not be enough to take the account over.
 *
 * Every *other* session on the account is signed out; this one is kept. Changing a
 * password because it may have leaked is the main reason to change one, and a leak
 * that left the leaker's session alive would make the change decorative.
 *
 * A service token has no password here — it is configured on the server — so the
 * form is not offered to one.
 */
export function Account() {
  const { principal } = useSession();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const change = useMutation({
    mutationFn: () =>
      post<PasswordChange>("/auth/password", { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
    },
  });

  const issues = passwordIssues(next, confirm);
  const canSubmit =
    current !== "" && next.length >= MIN_PASSWORD_LENGTH && confirm === next && !change.isPending;

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (canSubmit) change.mutate();
  }

  return (
    <>
      <PageHeader eyebrow="Account" title={principal?.name ?? "Your account"} />

      <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-3">
        <Datum label="Email">{principal?.email ?? "—"}</Datum>
        <Datum label="Roles">
          {principal === undefined || principal.roles.length === 0
            ? "—"
            : principal.roles.map((role) => role.name).join(" · ")}
        </Datum>
        <Datum label="Signed in as">
          {principal?.type === "service" ? "Service token" : "Operator"}
        </Datum>
      </dl>

      {/*
        The effective set, grouped, so "why can I not refund?" is answerable from
        one's own page rather than by asking an administrator to look.
      */}
      <SectionTitle>What you can do · {permissionCount(principal?.permissions ?? [])}</SectionTitle>
      <ul className="m-0 grid list-none grid-cols-1 gap-x-8 gap-y-3 p-0 md:grid-cols-2 xl:grid-cols-3">
        {groupPermissions(principal?.permissions ?? []).map((group) => (
          <li key={group.group} className="flex flex-col gap-1">
            <span className="text-data font-semibold text-ink">{group.group}</span>
            <span className="text-data text-ink-muted">
              {group.permissions.map((info) => info.label).join(", ")}
            </span>
          </li>
        ))}
      </ul>

      <SectionTitle>Password</SectionTitle>

      {principal?.type !== "user" ? (
        <p className="m-0 max-w-prose text-body text-ink-muted">
          This session uses a service token, which has no password to change here. Tokens are set in
          the server's configuration.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="flex w-full max-w-sm flex-col gap-6">
          {change.isSuccess ? (
            <Notice tone="ok">
              Password changed.{" "}
              {change.data.revokedSessions === 0
                ? "No other device was signed in."
                : `${change.data.revokedSessions} other ${
                    change.data.revokedSessions === 1 ? "session was" : "sessions were"
                  } signed out.`}
            </Notice>
          ) : null}
          {change.isError ? <ErrorNotice error={change.error} /> : null}

          <Field
            label="Current password"
            type="password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            required
          />
          <Field
            label="New password"
            type="password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            autoComplete="new-password"
            required
            hint={`At least ${MIN_PASSWORD_LENGTH} characters. Other devices will be signed out.`}
            {...(issues.password === undefined ? {} : { error: issues.password })}
          />
          <Field
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
            {...(issues.confirm === undefined ? {} : { error: issues.confirm })}
          />

          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            pending={change.isPending}
            className="self-start"
          >
            Change password
          </Button>
        </form>
      )}
    </>
  );
}
